import type { SummaryCard } from "../types";

import { SkeletonLoader } from "../../SkeletonLoader";
import { Typo } from "../../Typo";
import { usePerformanceReportTranslations } from "../hooks/usePerformanceReportTranslations";

/** Line height (px) of the `bodyXS` card label — reserved when no label is known. */
const LABEL_HEIGHT = 18;

/**
 * Mobile KPI strip — laid out as a horizontally-scrollable row instead of the
 * desktop `SummaryCards` reflowing grid (AC-3). Every card renders identically
 * (same tint, same value color) — the 2026-07-21 design audit re-verified the
 * live canvas (`js/app.jsx:1079`, one shared `.m-kpi` template, no last-card
 * modifier) gives Total earnings no visual distinction from the other four,
 * superseding AC-3's "Total earnings card is visually distinguished" text.
 *
 * Mirrors the desktop card's non-data states: skeleton while loading, `0` when
 * the query returned nothing (USD cards keep their unit), and a disabled `—`
 * on error.
 */
export const MobileSummaryCards = ({
  cards,
  isLoading,
  isError,
  isEmpty,
  skeletonCount = 5,
}: {
  cards: SummaryCard[];
  isLoading?: boolean;
  /** Values become a disabled `—` and the unit is dropped. */
  isError?: boolean;
  /** Values become `0`; USD cards keep their unit. */
  isEmpty?: boolean;
  skeletonCount?: number;
}) => {
  const { summaryCardLabels } = usePerformanceReportTranslations();

  const items: (SummaryCard | undefined)[] =
    cards.length > 0
      ? cards
      : isLoading || isError || isEmpty
        ? Array.from({ length: skeletonCount })
        : [];

  return (
    <div className="scrollbar-minimal-hover -mx-1 overflow-x-auto px-1">
      <div
        className="grid auto-cols-[minmax(9rem,1fr)] grid-flow-col gap-3"
        style={{ minWidth: "640px" }}
      >
        {items.map((card, index) => (
          <div
            key={card?.translationKey ?? index}
            className="flex flex-col gap-2 rounded-2xl bg-surface-brand-subtle p-4"
          >
            {card ? (
              <Typo
                element="span"
                type="bodyXS"
                textColor="text-text-secondary"
              >
                {summaryCardLabels[card.translationKey]}
              </Typo>
            ) : isLoading ? (
              <SkeletonLoader
                variant="rounded"
                motionReduce
                radius="md"
                width="70%"
                height={LABEL_HEIGHT}
              />
            ) : (
              <div aria-hidden="true" style={{ height: LABEL_HEIGHT }} />
            )}
            {isLoading ? (
              <SkeletonLoader
                variant="rounded"
                motionReduce
                radius="md"
                width="55%"
                height={24}
              />
            ) : (
              <Typo
                element="span"
                type="labelMD"
                textColor={isError ? "text-text-disabled" : "text-text-primary"}
              >
                {isError ? "—" : isEmpty ? "0" : card?.value}
                {!isError && card?.unit && (
                  <Typo
                    element="span"
                    type="labelXS"
                    textColor="text-text-secondary"
                    className="ml-1"
                  >
                    {card.unit}
                  </Typo>
                )}
              </Typo>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
